import {
    Button,
    DisplayOrInput,
    Input,
    Modal,
    ModalFooter,
    ModalHeader,
    ConfirmationModal,
    Field,
} from "modules/common";
import { useEffect, useMemo, useState } from "react";
import { RecordCategory } from "services/records";
import {
    useCreateRecordCategory,
    useDeleteRecordCategory,
    useEditRecordCategory,
} from "../hooks/recordsHooks";

export interface IRecordCategoryModal {
    recordCategory?: RecordCategory;
    isOpen: boolean;
    handleClose: () => void;
    hasRecords?: boolean;
}

const emptyRecordCategory: RecordCategory = {
    guid: "",
    name: "",
    createdBy: "",
    created: "",
    lastUpdatedBy: "",
    lastUpdated: "",
};

type RecordProperty = keyof RecordCategory;

type RecordCategoryErrors = Partial<Record<RecordProperty, string>>;

const validateRecord = (recordCategory: RecordCategory): RecordCategoryErrors => {
    const errors: RecordCategoryErrors = {};
    if (!recordCategory.name.trim()) {
        errors.name = "Naziv kategorije je obavezan";
    }

    return errors;
};

export const RecordCategoryModal = ({
    recordCategory: selectedRecordCategory,
    isOpen,
    handleClose,
    hasRecords,
}: IRecordCategoryModal) => {
    const [recordCategory, setRecordCategory] = useState<RecordCategory>(emptyRecordCategory);
    const [errors, setErrors] = useState<RecordCategoryErrors>({});
    const [isEdit, setIsEdit] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const create = useCreateRecordCategory();
    const edit = useEditRecordCategory();
    const remove = useDeleteRecordCategory();

    useEffect(() => {
        setRecordCategory(selectedRecordCategory ?? emptyRecordCategory);
    }, [selectedRecordCategory]);

    useEffect(() => {
        setErrors({});
        setIsEdit(false);
    }, [isOpen]);

    const isAdd = useMemo(() => !selectedRecordCategory, [selectedRecordCategory]);

    const title = useMemo(() => {
        if (isAdd) return "Nova kategorija";
        if (isEdit) return "Uredi kategoriju evidencije";
        return recordCategory?.name || "Detalji kategorije evidencije";
    }, [isAdd, isEdit, recordCategory?.name]);

    const changeRecordProperty = (
        property: RecordProperty,
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setErrors(prev => ({ ...prev, [property]: undefined }));
        setRecordCategory(prev => ({
            ...(prev ?? emptyRecordCategory),
            [property]: event.target.value,
        }));
    };

    const isValid = (property: RecordProperty) => !errors[property];
    const getErrorMsg = (property: RecordProperty) => errors[property] ?? "";

    const onCancel = () => {
        setIsEdit(false);
        setErrors({});
        setRecordCategory(selectedRecordCategory ?? emptyRecordCategory);
    };

    const onSave = async () => {
        const newErrors = validateRecord(recordCategory);
        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }

        const mutation = isAdd ? create.mutateAsync : edit.mutateAsync;
        await mutation(recordCategory);
        handleClose();
    };

    const onDelete = async () => {
        if (!recordCategory) return;
        await remove.mutateAsync(recordCategory.guid);
        setShowDelete(false);
        handleClose();
    };

    return (
        <Modal
            isActive={isOpen}
            className="record-category-modal"
            onClose={handleClose}
            modalHeader={<ModalHeader onClose={handleClose} title={title} />}
            modalFooter={
                recordCategory ? (
                    <ModalFooter>
                        {isAdd || isEdit ? (
                            <>
                                {isEdit && (
                                    <Button
                                        type="button"
                                        size="small"
                                        variant="primary-ghost"
                                        onClick={onCancel}
                                    >
                                        <span>ODUSTANI</span>
                                    </Button>
                                )}
                                <Button
                                    type="button"
                                    size="small"
                                    variant="primary"
                                    onClick={onSave}
                                >
                                    <span>SPREMI</span>
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    type="button"
                                    size="small"
                                    variant="primary-ghost"
                                    onClick={() => setIsEdit(true)}
                                >
                                    <span>UREDI</span>
                                </Button>
                                <Button
                                    type="button"
                                    size="small"
                                    variant="warning"
                                    disabled={hasRecords ?? false}
                                    onClick={() => setShowDelete(true)}
                                >
                                    <span>OBRIŠI</span>
                                </Button>
                                {hasRecords && (
                                    <p className="error-message">
                                        Nije moguće obrisati kategoriju koja sadrži evidencije.
                                    </p>
                                )}
                            </>
                        )}
                    </ModalFooter>
                ) : null
            }
        >
            <div className="record-category">
                <div className="modal-section">
                    <Field name="Naziv kategorije" errorMessage={getErrorMsg("name")}>
                        <DisplayOrInput isEdit={isAdd || isEdit}>
                            {recordCategory?.name}
                            <Input
                                id="name"
                                type="text"
                                size="small"
                                name="name"
                                isValid={isValid("name")}
                                value={recordCategory?.name}
                                onChange={changeRecordProperty.bind(null, "name")}
                            />
                        </DisplayOrInput>
                    </Field>
                </div>
            </div>
            <ConfirmationModal
                isActive={showDelete}
                onConfirm={onDelete}
                onCancel={() => setShowDelete(false)}
                content={`Želite li izbrisati evidenciju ${recordCategory?.name}?`}
                confirmText="OBRIŠI"
                cancelText="ODUSTANI"
            />
        </Modal>
    );
};
